import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Select from '../components/ui/Select';
import { Wallet, ArrowDownCircle, ArrowUpCircle, TrendingUp } from 'lucide-react';

export default function WalletPage() {
  const [typeFilter, setTypeFilter] = useState('');
  const { data } = useQuery({ queryKey: ['walletSummary'], queryFn: () => api.get('/wallet/summary').then(r => r.data) });
  const wallet = data?.wallets?.[0];
  const transactions = data?.transactions || [];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="d-page-header">
        <h1 className="text-2xl font-bold text-white">Wallet</h1>
        <p className="text-slate-400 mt-1">Manage your funds and transaction history.</p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="d-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Available Balance</p>
              <p className="text-xl font-bold text-white">{formatCurrency(wallet?.balance || 0)}</p>
            </div>
          </div>
        </div>
        <div className="d-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center">
              <ArrowDownCircle className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Deposits</p>
              <p className="text-xl font-bold text-white">{formatCurrency(data?.summary?.totalDeposits || 0)}</p>
            </div>
          </div>
        </div>
        <div className="d-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Earnings</p>
              <p className="text-xl font-bold text-white">{formatCurrency(data?.summary?.totalEarnings || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-white">Transaction History</h2>
            <Select
              options={[
                { value: '', label: 'All Types' },
                { value: 'DEPOSIT', label: 'Deposit' },
                { value: 'WITHDRAWAL', label: 'Withdrawal' },
                { value: 'ROI_EARNING', label: 'ROI' },
                { value: 'DIRECT_COMMISSION', label: 'Direct' },
                { value: 'LEVEL_INCOME', label: 'Level Income' },
                { value: 'BONUS', label: 'Bonus' },
              ]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-auto"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="d-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.filter((tx: any) => !typeFilter || tx.type === typeFilter).length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-500">No transactions found</td></tr>
                ) : (
                  transactions.filter((tx: any) => !typeFilter || tx.type === typeFilter).map((tx: any) => (
                    <tr key={tx.id}>
                      <td><Badge status={tx.type} /></td>
                      <td className="text-sm font-medium text-white">{formatCurrency(tx.amount)}</td>
                      <td><Badge status={tx.status} /></td>
                      <td className="text-sm text-slate-400">{tx.description || '-'}</td>
                      <td className="text-sm text-slate-400">{new Date(tx.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
